import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useSupplierMatches } from "@/queries/customer/supplier-match";
import {
  dismissSupplierMatchOnboarding,
  isSupplierMatchOnboardingDismissed
} from "@/utils/supplierMatchDismissal";
import { Link } from "@tanstack/react-router";
import { ChevronRight, Handshake } from "lucide-react";
import { useState } from "react";

/**
 * Home dashboard Supplier Match summary (ADR-0005 "Summary widget" +
 * "Onboarding"). One component, two states, both driven by the same grid
 * query the full settings page uses:
 *
 * - **Onboarding invite** - shown while every seller is still `not_checked`
 *   and the salon hasn't dismissed it. Skippable, never a blocking gate; a
 *   salon who skips still checks out at default/trade pricing.
 * - **Compact summary** - shown once dismissed or once any seller has
 *   settled, linking out to the same full page either way.
 */
export function SupplierMatchWidget() {
  const { data, isLoading, error } = useSupplierMatches();
  const [dismissed, setDismissed] = useState(
    isSupplierMatchOnboardingDismissed
  );

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-4 p-5" aria-hidden="true">
          <Skeleton className="h-10 w-10 flex-shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-3 w-32" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-5">
          <p className="text-sm text-red-600 dark:text-red-400">
            Could not load Supplier Match.
          </p>
        </CardContent>
      </Card>
    );
  }

  const matches = data?.supplier_matches ?? [];
  const hasSettledRow = matches.some((match) => match.status !== "not_checked");
  const showInvite = !dismissed && !hasSettledRow;

  const icon = (
    <div
      className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
      aria-hidden="true"
    >
      <Handshake className="h-5 w-5" />
    </div>
  );

  if (showInvite) {
    return (
      <Card>
        <CardContent className="flex flex-col items-start gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            {icon}
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                Set up Supplier Match
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                See which suppliers recognize your account and get your
                negotiated prices automatically.
              </p>
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2 self-end sm:self-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                dismissSupplierMatchOnboarding();
                setDismissed(true);
              }}
            >
              Skip for now
            </Button>
            <Button size="sm" asChild>
              <Link to="/account/supplier-match">Set up</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // A claimed trade account (KAN-322) counts as matched here.
  const isSettled = (match: (typeof matches)[number]) =>
    match.status === "matched" || match.status === "claimed";
  const matchedCount = matches.filter(isSettled).length;
  // `lookup_capable` and `claimable` are live/current facts (the seller's
  // platform connection right now), while `status` is persisted - a seller
  // matched while capable can later lose capability (disconnect) and keep
  // status: "matched" (ADR-0005: the grid still shows the last-known status),
  // and a claim outlives its seller becoming lookup-capable. Counting only
  // live sellers in the denominator could then undercut the numerator (e.g.
  // "1 of 0 suppliers matched") - always include an already matched or
  // claimed seller in the denominator too.
  const matchableCount = matches.filter(
    (match) => match.lookup_capable || match.claimable || isSettled(match)
  ).length;

  return (
    <Card>
      <CardContent className="flex items-center justify-between gap-4 p-5">
        <div className="flex items-center gap-4">
          {icon}
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              Supplier Match
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {matchedCount} of {matchableCount} suppliers matched
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          asChild
          className="flex-shrink-0 text-purple-600 hover:text-purple-700 dark:text-purple-400"
        >
          <Link to="/account/supplier-match">
            View all
            <ChevronRight className="ml-1 h-4 w-4" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
